import { gql } from '@apollo/client';
import { ColorInput, FormField, Text } from '@mnd-frontend/ui/publish/components';
import { useToasts } from '@mnd-frontend/ui/publish/contexts';
import { PURPLE_700, RED_700, SPACING_2 } from '@mnd-frontend/ui/shared/tokens';
import { isEqual } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import ImageUploader from '../../../components/MediaUploaders/ImageUploader';
import { useCurrentPressroom } from '../../../contexts/CurrentPressroomContext';
import { useEnvironmentVariables } from '../../../contexts/EnvironmentVariablesContext';
import {
  type NewsroomDataBranding,
  useNewsroomSetup,
} from '../../../contexts/NewsroomSetupContext';
import { useUpdateBrandingStylesMutation } from '../../../generated/hooks';
import {
  type ImageUploadResponse,
  uploadImageToCloudinary,
} from '../../../hooks/useCloudinaryImageUpload';
import { track } from '../../../lib/tracker';
import Footer from './Footer';
import Header from './Header';
import { ImgUploaderWrapper, StepForm, StepSectionWrapper } from './styles';

gql`
  fragment Branding on NewsroomStyle {
    primaryBrandColor
    secondaryBrandColor
    logotype {
      id
      cloudinaryId
      url
    }
    favicon {
      id
      url(transformation: THUMBNAIL_SMALL, cropped: false)
    }
  }

  mutation updateBrandingStyles(
    $pressroomId: ID!
    $logotype: ResourceImageInput
    $deleteLogotype: Boolean
    $primaryBrandColor: String
    $secondaryBrandColor: String
  ) {
    newsroomSettingsUpdate(
      input: {
        newsroomId: $pressroomId
        logotype: $logotype
        deleteLogotype: $deleteLogotype
        primaryBrandColor: $primaryBrandColor
        secondaryBrandColor: $secondaryBrandColor
      }
    ) {
      error {
        message
        ... on NewsroomSettingsUpdateValidationError {
          primaryBrandColor
          secondaryBrandColor
          logotype {
            fileFormat
            name
          }
        }
      }
      newsroomSettings {
        id
        newsroomStyle {
          ...Branding
        }
      }
    }
  }

  mutation previewBrandingStyles(
    $newsroomId: ID!
    $primaryBrandColor: String
    $secondaryBrandColor: String
    $logotype: ResourceImageInput
    $deleteLogotype: Boolean
  ) {
    newsroomStylePreviewUpdate(
      input: {
        newsroomId: $newsroomId
        primaryBrandColor: $primaryBrandColor
        secondaryBrandColor: $secondaryBrandColor
        logotype: $logotype
        deleteLogotype: $deleteLogotype
      }
    ) {
      newsroomStyle {
        ...Branding
      }
      pressroom {
        id
        newsroomUrl
      }
    }
  }
`;

const validFileFormats = ['.jpg', '.jpeg', '.gif', '.png', '.svg'];
const minWidth = 200;
const minHeight = 200;
const maxWidth = 4096;
const maxHeight = 4096;

const BRANDING_FONT_STYLES = [{ fontSize: 16, fontWeight: 400 }];

const ColorInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING_2};
`;

const Branding = () => {
  const [t] = useTranslation();
  const { showFailureToast } = useToasts();
  const { mndAssets } = useEnvironmentVariables();
  const { sourceId, pressroomId } = useCurrentPressroom();
  const { newsroomData, handleNewsroomDataFieldChange, setNewsroomSetupStep } = useNewsroomSetup();

  const formRef = useRef<HTMLFormElement>(null);
  const initialValues = useRef<NewsroomDataBranding>(newsroomData.branding);

  const [imageUploadResponse, setImageUploadResponse] = useState<ImageUploadResponse | null>(null);

  const [save, { data, loading }] = useUpdateBrandingStylesMutation({
    onCompleted: response => {
      const responseError = response.newsroomSettingsUpdate?.error;
      if (responseError) {
        if (responseError.message) {
          showFailureToast(responseError.message);
        }
      } else {
        const dataChanged = !isEqual(initialValues.current, newsroomData.branding);
        track({
          event: 'Newsroom Setup: Branding - Proceed',
          traits: {
            step: 1,
            dataChanged,
          },
        });
        setNewsroomSetupStep('press-contact');
      }
    },
    onError: () => showFailureToast(t('common_general_error')),
    variables: {
      logotype: imageUploadResponse
        ? {
            cloudinaryResponse: {
              publicId: imageUploadResponse.public_id,
              version: imageUploadResponse.version,
              bytes: imageUploadResponse.bytes,
              format: imageUploadResponse.format,
              height: imageUploadResponse.height,
              width: imageUploadResponse.width,
            },
            name: imageUploadResponse.original_filename,
            fileName: imageUploadResponse.original_filename,
          }
        : undefined,
      deleteLogotype: !newsroomData.branding.logoCloudinaryId && !imageUploadResponse,
      pressroomId,
      primaryBrandColor: newsroomData.branding.primaryBrandColor,
      secondaryBrandColor: newsroomData.branding.secondaryBrandColor,
    },
  });

  const validationError =
    data?.newsroomSettingsUpdate?.error?.__typename === 'NewsroomSettingsUpdateValidationError'
      ? data.newsroomSettingsUpdate.error
      : undefined;

  useEffect(() => {
    if (validationError || formRef.current) {
      formRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [validationError]);

  useEffect(() => {
    initialValues.current = newsroomData.branding;
    const prepopulated =
      newsroomData.branding.primaryBrandColor !== RED_700 &&
      newsroomData.branding.primaryBrandColor !== PURPLE_700;

    track({
      event: 'Newsroom Setup: Branding',
      traits: {
        step: 1,
        prepopulated,
      },
    });
  }, [newsroomData.branding]);

  return (
    <StepForm
      ref={formRef}
      onSubmit={e => {
        e.preventDefault();
        save();
      }}
    >
      <StepSectionWrapper>
        <Header currentStep={1} heading={t('onboarding-branding_title')} />
        <ColorInputContainer>
          <Text as="p" variant="body">
            {t('newsroom-setup_brand-colors')}
          </Text>
          <div>
            <ColorInput
              value={newsroomData.branding.primaryBrandColor}
              onChange={e =>
                handleNewsroomDataFieldChange('branding', 'primaryBrandColor', e.target.value)
              }
              helperText={t(
                'newsroom_settings-design_settings-brand_colors-input_label_description',
              )}
            />
          </div>
          <div>
            <ColorInput
              value={newsroomData.branding.secondaryBrandColor}
              onChange={e =>
                handleNewsroomDataFieldChange('branding', 'secondaryBrandColor', e.target.value)
              }
              checkContrast
              fontStyles={BRANDING_FONT_STYLES}
              helperText={t(
                'newsroom_settings-design_settings-secondary_colors-input_label_description',
              )}
            />
          </div>
        </ColorInputContainer>
        <FormField error={validationError?.logotype?.fileFormat || validationError?.logotype?.name}>
          {/** biome-ignore lint/complexity/noUselessFragments: legacy formfield needs it */}
          <>
            <Text as="p" variant="body" marginBottom="SPACING_2">
              {t('newsroom-setup_logotype')}
            </Text>
            <ImgUploaderWrapper>
              <ImageUploader
                cloudinaryId={newsroomData.branding.logoCloudinaryId}
                caption={t('components_image-upload_caption', {
                  types: `${validFileFormats.join(', ')}`,
                  minWidth,
                  minHeight,
                })}
                action={async (file: File) => {
                  const resp = await uploadImageToCloudinary(
                    file,
                    sourceId,
                    () => showFailureToast(t('common_general_error')),
                    mndAssets,
                  );
                  if (!resp) return;
                  setImageUploadResponse(resp);
                  handleNewsroomDataFieldChange('branding', 'logoCloudinaryId', resp.public_id);
                }}
                deleteCallback={() =>
                  handleNewsroomDataFieldChange('branding', 'logoCloudinaryId', null)
                }
                validatorProps={{
                  validFileFormats,
                  validateMIMEType: 'image',
                  minWidth,
                  minHeight,
                  maxWidth,
                  maxHeight,
                  maxSizeInBytesSvg: 50 * 1024,
                }}
              />
            </ImgUploaderWrapper>
          </>
        </FormField>
        <Footer
          isSaving={loading}
          previousStep={undefined}
          nextStep="press-contact"
          canContinue={
            !!(newsroomData.branding.primaryBrandColor || newsroomData.branding.secondaryBrandColor)
          }
        />
      </StepSectionWrapper>
    </StepForm>
  );
};

export default Branding;
